import { useMemo, useState } from 'react';
import { AlertTriangle, Check } from 'lucide-react';
import { ErrorMessage } from '@/components/ui/error-message';
import { WorkspaceButton } from '@/components/ui/workspace/button';
import { WorkspaceInput, WorkspaceSelect } from '@/components/ui/workspace/field';
import {
  rubricSumOffenders,
  usePatchRubricCriteriaMutation,
  type HrCriterionGroup,
} from '@/lib/api/health-readiness';
import { cn } from '@/lib/utils/cn';
import { formatPercent } from './model';

/**
 * Manage criteria and weights (T-0222 / E20-F01): the weights table with
 * the live sum rule, Save disabled until the weights reach 100%, add a
 * criterion (weight 0%), retire one (its weight must be given to others
 * before saving). Mirrors `manageCriteria()` in the prototype.
 *
 * Save sends the whole set to PATCH …/readiness/rubric as fractions; the
 * API refuses a set that does not sum to 1 and names the criteria to
 * adjust, which the screen shows under the table (rule 3: nothing is
 * normalised silently, here or there).
 *
 * T-0238: each criterion carries a description — what 100%, 50% and 0%
 * look like. The AI scores the criterion against it, so a new criterion
 * needs only its name, weight and description.
 */
export interface ManageCriteriaProps {
  engagementId: string;
  groups: HrCriterionGroup[];
  onDone: () => void;
  /** Fires once the API accepted the set. */
  onSaved?: () => void;
}

interface DraftRow {
  key: string;
  id?: string;
  name: string;
  category: string;
  /** The weight as typed, in percent. */
  weightPct: string;
  /** What 100%, 50% and 0% look like, as typed. */
  description: string;
  scorePercent: number | null;
  retired: boolean;
}

/** Sum of the active rows' weights in percent, to one decimal. */
export function sumWeights(rows: ReadonlyArray<Pick<DraftRow, 'weightPct' | 'retired'>>): number {
  const total = rows.reduce((acc, r) => (r.retired ? acc : acc + (Number(r.weightPct) || 0)), 0);
  return Math.round(total * 10) / 10;
}

export function weightsAddUp(sum: number): boolean {
  return Math.abs(sum - 100) <= 0.05;
}

export function ManageCriteria({ engagementId, groups, onDone, onSaved }: ManageCriteriaProps) {
  const [rows, setRows] = useState<DraftRow[]>(() =>
    groups.flatMap((g) =>
      g.criteria.map((c) => ({
        key: c.id,
        id: c.id,
        name: c.name,
        category: c.category,
        weightPct: String(Math.round(c.weight * 1000) / 10),
        description: c.rubricText ?? '',
        scorePercent: c.scorePercent,
        retired: false,
      })),
    ),
  );
  const categories = useMemo(() => {
    const set = new Set<string>(groups.map((g) => g.category));
    for (const r of rows) set.add(r.category);
    return Array.from(set);
  }, [groups, rows]);
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState(categories[0] ?? '');
  const patch = usePatchRubricCriteriaMutation(engagementId);

  const sum = sumWeights(rows);
  const bad = !weightsAddUp(sum);
  const offenders = rubricSumOffenders(patch.error);

  const update = (key: string, change: Partial<DraftRow>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...change } : r)));

  const save = () => {
    if (bad) return;
    patch.mutate(
      {
        criteria: rows
          .filter((r) => r.id !== undefined || !r.retired)
          .map((r) => ({
            ...(r.id ? { id: r.id } : {}),
            name: r.name,
            category: r.category,
            description: r.description.trim().length > 0 ? r.description.trim() : null,
            weight: (Number(r.weightPct) || 0) / 100,
            ...(r.retired ? { retire: true } : {}),
          })),
      },
      {
        onSuccess: () => {
          onSaved?.();
          onDone();
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-4" data-testid="manage-criteria">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="t-h1">Manage criteria and weights</h1>
          <div className="mt-0.5 text-meta text-ink-500">
            Weights must add up to 100%. The go-live verdict is recomputed from the confirmed scores
            the moment you save.
          </div>
        </div>
        <div className="flex gap-2">
          <WorkspaceButton variant="tertiary" onClick={onDone} disabled={patch.isPending}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton variant="primary" onClick={save} disabled={bad || patch.isPending}>
            {patch.isPending ? 'Saving…' : 'Save weights'}
          </WorkspaceButton>
        </div>
      </div>

      <section
        aria-label="Criteria and weights"
        className="overflow-x-auto rounded-card border border-ink-100 bg-paper-0"
      >
        <table className="w-full border-collapse text-body-sm text-ink-700">
          <thead>
            <tr>
              <Th>Category</Th>
              <Th>Criterion</Th>
              <Th num>Score</Th>
              <Th num>Weight %</Th>
              <Th>
                <span className="sr-only">Actions</span>
              </Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className={cn(r.retired && 'text-ink-300')}>
                <Td>{r.category}</Td>
                <Td>
                  <span className={cn(r.retired && 'line-through')}>{r.name}</span>
                  {r.id === undefined ? (
                    <span className="ml-2 text-meta text-ink-500">new</span>
                  ) : null}
                  {r.retired ? null : (
                    <textarea
                      value={r.description}
                      onChange={(e) => update(r.key, { description: e.target.value })}
                      aria-label={`Description of ${r.name}`}
                      placeholder="What 100%, 50% and 0% look like"
                      rows={2}
                      maxLength={2000}
                      className="mt-1 block w-full min-w-[16rem] rounded-chip border border-ink-200 bg-paper-0 px-1.5 py-1 text-meta text-ink-900"
                    />
                  )}
                </Td>
                <Td num>{formatPercent(r.scorePercent)}</Td>
                <Td num>
                  {r.retired ? (
                    '—'
                  ) : (
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.5}
                      value={r.weightPct}
                      onChange={(e) => update(r.key, { weightPct: e.target.value })}
                      aria-label={`Weight of ${r.name}`}
                      className="h-7 w-16 rounded-chip border border-ink-200 bg-paper-0 px-1.5 text-right font-mono text-body-sm text-ink-900"
                    />
                  )}
                </Td>
                <Td>
                  {r.retired ? (
                    <WorkspaceButton
                      variant="tertiary"
                      onClick={() => update(r.key, { retired: false })}
                    >
                      Undo
                    </WorkspaceButton>
                  ) : (
                    <WorkspaceButton
                      variant="tertiary"
                      onClick={() => update(r.key, { retired: true })}
                      aria-label={`Retire ${r.name}`}
                    >
                      Retire
                    </WorkspaceButton>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <p
        role="status"
        data-testid="sum-rule"
        className={cn(
          'flex items-center gap-2 text-body-sm',
          bad ? 'text-posture-critical' : 'text-ink-700',
        )}
      >
        {bad ? (
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        Weights add up to {sum}%{bad ? ' — adjust until they reach 100%' : ''}
      </p>

      {patch.error ? (
        <div className="flex flex-col gap-1.5">
          <ErrorMessage error={patch.error} />
          {offenders.length > 0 ? (
            <p className="text-body-sm text-posture-critical">Adjust: {offenders.join(', ')}.</p>
          ) : null}
        </div>
      ) : null}

      <form
        aria-label="Add a criterion"
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const name = newName.trim();
          if (!name || !newCategory) return;
          setRows((prev) => [
            ...prev,
            {
              key: `new-${Date.now()}-${prev.length}`,
              name,
              category: newCategory,
              weightPct: '0',
              description: '',
              scorePercent: null,
              retired: false,
            },
          ]);
          setNewName('');
        }}
      >
        <WorkspaceInput
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Add a criterion, e.g. Payroll parallel run"
          aria-label="New criterion name"
          className="h-[30px] max-w-sm flex-1 text-body-sm"
        />
        <WorkspaceSelect
          value={newCategory}
          onChange={(e) => setNewCategory(e.target.value)}
          aria-label="New criterion category"
          className="h-[30px] w-auto text-body-sm"
        >
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </WorkspaceSelect>
        <WorkspaceButton type="submit" variant="secondary" disabled={newName.trim().length === 0}>
          Add with weight 0%
        </WorkspaceButton>
      </form>
    </div>
  );
}

function Th({ children, num }: { children: React.ReactNode; num?: boolean }) {
  return (
    <th
      className={cn(
        'border-b border-ink-100 px-2 py-1.5 text-left text-[11px] font-medium uppercase leading-[18px] tracking-[0.06em] text-ink-500',
        num && 'text-right',
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, num }: { children: React.ReactNode; num?: boolean }) {
  return (
    <td
      className={cn(
        'border-b border-ink-100 px-2 py-2 align-middle',
        num && 'text-right font-mono tabular-nums',
      )}
    >
      {children}
    </td>
  );
}
